import React, { useState, useEffect } from "react";
import Header from "./Header";
import { fetchProtocolScore } from "../services/defiService";
import { trackReportView } from "../utils/analytics";
import GradeLegend from "./GradeLegend";
import { normalizeSubscores, getDimensionLabel, sortSubscoresByWeight } from "../utils/scoreUtils";

// 根据分数计算评级
function calculateGrade(score) {
  if (score === null || score === undefined || typeof score !== 'number') {
    return 'N/A';
  }
  
  // Litepaper 标准：5 级评分系统
  if (score >= 90) return 'AAA';
  if (score >= 75) return 'AA-A';
  if (score >= 60) return 'BBB-B';
  if (score >= 40) return 'CCC';
  return 'D';
}

// 格式化时间戳
function formatTimeAgo(timestamp) {
  if (!timestamp) return "Unknown";
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return "Unknown";
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 0) {
      return "just now";
    }
    
    if (diffInSeconds < 60) {
      return "just now";
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
  } catch (error) {
    console.error("Error formatting time ago:", error);
    return "Unknown";
  }
}

function Report({ slug }) {
  const [protocol, setProtocol] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    const loadBySlug = async () => {
      if (!slug) {
        setError("Invalid slug");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setReportLoading(true);
        setReportError(null);

        // 直接通过 slug 调 /getScore?id={slug}
        const reportData = await fetchProtocolScore(slug);
        setReport(reportData);

        // 优先使用后端附带的 protocol 元信息（如果有）
        if (reportData && reportData.protocol) {
          setProtocol(reportData.protocol);
          // 跟踪报告查看
          trackReportView(reportData.protocol.name, reportData.protocol.id);
        } else if (reportData && reportData.protocol_name) {
          // 兼容旧格式，从报告里拼一个最小的 protocol 对象
          setProtocol({
            id: reportData.protocol_id,
            name: reportData.protocol_name,
            tvl: reportData.tvl || 0,
            category: reportData.category || 'Other',
            blockchain: reportData.blockchain || 'Unknown',
            logo: reportData.logo || null,
            url: reportData.homepage_url || null,
            app_url: reportData.app_url || null,
            description: reportData.description || '',
            score: reportData.overall_risk?.risk_score ?? null,
          });
        } else {
          setError("Protocol not found");
        }
      } catch (err) {
        setError("Failed to load protocol");
        setReportError("Unable to fetch report. Please try again later.");
        console.error("Error loading report by slug:", err);
      } finally {
        setReportLoading(false);
        setLoading(false);
      }
    };

    loadBySlug();
  }, [slug]);

  const hasScore = protocol && typeof protocol.score === "number";
  const scoreValue = hasScore ? Math.round(protocol.score) : null;
  const scoreBadgeColor = hasScore
    ? scoreValue >= 90
      ? "bg-green-100 text-green-800"
      : scoreValue >= 75
      ? "bg-yellow-100 text-yellow-800"
      : "bg-rose-100 text-rose-800"
    : "bg-gray-100 text-gray-500";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-gray-600">Loading protocol...</div>
        </div>
      </div>
    );
  }

  if (error || !protocol) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-center">
            <div className="text-red-600 mb-4">{error || "Protocol not found"}</div>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const homepageUrl = protocol.url && protocol.url !== "#" ? protocol.url : null;
  const appUrl = protocol.app_url || null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Protocol Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 relative">
          {/* Score in top right corner */}
          {hasScore && (
            <div className="absolute top-6 right-6">
              <div className={`inline-flex flex-col items-center justify-center rounded-lg px-4 py-2 shadow-sm ${scoreBadgeColor}`}>
                <div className="flex items-center">
                  <span className="text-2xl font-bold">{scoreValue}</span>
                  <span className="ml-2 text-sm font-semibold">
                    {calculateGrade(protocol.score)}
                  </span>
                </div>
                {(protocol.lastScoreUpdatedAt || report?.timestamp) && (
                  <div className="text-xs opacity-75 mt-1">
                    Updated {formatTimeAgo(protocol.lastScoreUpdatedAt || report?.timestamp)}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-start gap-4 pr-32">
            {protocol.logo ? (
              <img
                src={protocol.logo}
                alt={`${protocol.name} logo`}
                className="w-16 h-16 rounded-xl flex-shrink-0 border border-gray-100 object-cover bg-gray-50"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='24' fill='%239ca3af'%3E" + (protocol.name?.charAt(0)?.toUpperCase() || '?') + "%3C/text%3E%3C/svg%3E";
                }}
              />
            ) : (
              <div className="w-16 h-16 rounded-xl flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-400">
                  {protocol.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{protocol.name}</h1>
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                  {protocol.category}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  {protocol.blockchain}
                </span>
              </div>
              
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    TVL
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    ${(protocol.tvl / 1e6).toFixed(2)}M
                  </div>
                </div>
              </div>
            </div>
          </div>

          {protocol.description && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Summary
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{protocol.description}</p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100 flex gap-3">
            {homepageUrl && (
              <a
                href={homepageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 shadow-sm hover:shadow transition-colors font-semibold"
              >
                Website
              </a>
            )}
            {appUrl && (
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
              >
                Launch App
              </a>
            )}
          </div>
        </div>

        {/* Report Section */}
        {hasScore ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Credit Report</h2>
            
            {reportLoading && (
              <div className="text-center text-gray-500 py-8">Loading report...</div>
            )}

            {reportError && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-600 mb-6">
                {reportError}
              </div>
            )}

            {!reportLoading && !reportError && report && (
              <div className="space-y-6">
                {/* 显示上一次分数和分数变化 */}
                {report?.previous_score !== undefined && report?.previous_score !== null && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">
                          Previous Rating
                        </div>
                        <div className="text-lg font-bold text-blue-900">
                          {Math.round(report.previous_score)} ({calculateGrade(report.previous_score)})
                        </div>
                      </div>
                      {report?.score_change !== undefined && report.score_change !== 0 && (
                        <div className="flex flex-col items-end">
                          <div className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">
                            Change
                          </div>
                          <div className={`text-2xl font-bold ${
                            report.score_change > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {report.score_change > 0 ? '+' : ''}{Math.round(report.score_change)}
                          </div>
                          <div className={`text-sm font-semibold ${
                            report.score_change > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {report.score_change > 0 ? '↑ Improved' : '↓ Declined'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                  {report?.overall_risk?.risk_summary_text && (
                <div className="rounded-lg bg-gray-50 p-6">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {report.overall_risk.risk_summary_text}
                    </p>
                  </div>
                  )}

                {report?.top_risk_drivers?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Risk Drivers</h3>
                    <ul className="list-disc space-y-2 pl-5 text-sm text-gray-600">
                      {report.top_risk_drivers.map((item, index) => (
                        <li key={`risk-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {report?.investment_suggestion?.actionable_advice?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Actionable Advice</h3>
                    <ul className="list-disc space-y-2 pl-5 text-sm text-gray-600">
                      {report.investment_suggestion.actionable_advice.map((advice, index) => (
                        <li key={`advice-${index}`}>{advice}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Subscores Section - 统一显示所有维度的评分 */}
                {report?.subscores && Object.keys(report.subscores).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Scores</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sortSubscoresByWeight(report.subscores).map(([key, subscore]) => {
                        const score = typeof subscore === 'object' ? subscore.score : subscore;
                        const weight = typeof subscore === 'object' ? subscore.weight : null;
                        const explanation = typeof subscore === 'object' ? subscore.explanation : null;
                        const label = getDimensionLabel(key, 'en');
                        
                        return (
                          <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-gray-900">{label}</span>
                              <div className="flex items-center gap-2">
                                {weight && (
                                  <span className="text-xs text-gray-500">({weight}%)</span>
                                )}
                                <span className="text-lg font-bold text-gray-900">{score}</span>
                              </div>
                            </div>
                            {explanation && (
                              <p className="text-xs text-gray-600 mt-2 leading-relaxed">{explanation}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {report?.disclaimer?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Disclaimers</h3>
                    <div className="rounded-md bg-gray-50 p-4 text-xs text-gray-500 space-y-2">
                      {report.disclaimer.map((item, index) => (
                        <p key={`disclaimer-${index}`}>{item}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-gray-500">
            No credit score available for this protocol
          </div>
        )}
      </main>
    </div>
  );
}

export default Report;

